import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { UserAccount, UserAccountPatch } from "@scandi/shared";
import { api } from "@/lib/api";
import { useFailureToast } from "@/hooks/use-failure-toast";
import { sessionKey, useSession } from "@/hooks/use-session";

export const usersKey = ["admin", "users"] as const;

/** Waiting for a role: signed in at least once, not approved, not switched off. */
export const isPendingAccount = (account: UserAccount): boolean =>
  account.role === null && !account.disabled;

export function useUsers({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: usersKey, queryFn: api.admin.users.list, enabled });
}

/**
 * How many people are waiting for access, for the badge beside "Users" in the
 * navigation.
 *
 * Asked only by admins: the endpoint refuses everyone else, and a refusal would
 * be a request wasted on every page. Refreshed every few minutes and on focus,
 * since the whole point is noticing someone new without opening the page.
 */
export function usePendingUsersCount(): number {
  const { isAdmin, session } = useSession();
  // The session carries the count for an admin: a number instead of the whole user list. Re-read on the
  // same clock the list was, so a new account is still noticed without opening the page. An older API
  // without it falls back to counting the list.
  const fromSession = session?.pendingUsers;
  useQuery({
    queryKey: sessionKey,
    queryFn: api.session,
    enabled: isAdmin && fromSession !== undefined,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: false,
  });
  const { data } = useQuery({
    queryKey: usersKey,
    queryFn: api.admin.users.list,
    enabled: isAdmin && fromSession === undefined,
    staleTime: 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
  if (!isAdmin) return 0;
  if (fromSession !== undefined) return fromSession;
  return data ? data.filter(isPendingAccount).length : 0;
}

export function useUpdateUser() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: UserAccountPatch }) =>
      api.admin.users.update(id, patch),
    onSuccess: (saved) => {
      queryClient.setQueryData<UserAccount[]>(usersKey, (list) =>
        list?.map((account) => (account.id === saved.id ? saved : account)),
      );
      queryClient.invalidateQueries({ queryKey: usersKey });
      // The badge's count rides on the session, and an admin renaming themselves sees it in their own menu.
      queryClient.invalidateQueries({ queryKey: sessionKey, exact: true });
      toast.success(t("users.toasts.saved", { name: accountName(saved) }));
    },
    onError,
  });
}

export function useDeleteUser() {
  const { t } = useTranslation();
  const onError = useFailureToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (account: UserAccount) => api.admin.users.remove(account.id),
    onSuccess: (_, account) => {
      queryClient.setQueryData<UserAccount[]>(usersKey, (list) =>
        list?.filter((row) => row.id !== account.id),
      );
      queryClient.invalidateQueries({ queryKey: usersKey });
      // The badge's count rides on the session.
      queryClient.invalidateQueries({ queryKey: sessionKey, exact: true });
      toast.success(t("users.toasts.deleted", { name: accountName(account) }));
    },
    onError,
  });
}

/** What the portal calls someone: the admin-given name, then Google's, then the address. */
export function accountName(account: UserAccount): string {
  return account.displayName ?? account.googleName ?? account.email;
}
